import { type FC, useMemo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";
import { FormBuilder } from "@/shared/components/Form";
import type { Schema } from "@/shared/components/Form/types";
import { ErrorMessage } from "@/shared/components";
import { validateWithZod } from "@/shared/utils/zod";
import {
  getErrorMessage,
  getGraphQLErrorCode,
} from "@/shared/utils/errorCodes";
import {
  useOrganizationBillingRequisitesQuery,
  useUpdateOrganizationBillingRequisitesMutation,
  type OrganizationBillingRequisites as OrganizationBillingRequisitesData,
} from "@/admin/entities/organizations";
import { makeOrganizationBillingRequisitesValidationSchema } from "./validation";

interface OrganizationBillingRequisitesProps {
  id: string;
}

const schema: Schema = {
  companyName: {
    type: "text",
    label: "Юрлицо",
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
  registrationNumber: {
    type: "text",
    label: "Регистрационный номер",
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
  vatNumber: {
    type: "text",
    label: "Налоговый номер (VAT)",
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
  address: {
    type: "text",
    label: "Адрес",
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
  contactName: {
    type: "text",
    label: "Контактное лицо",
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
  contactEmail: {
    type: "email",
    label: "Email контакта",
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
  invoiceEmails: {
    type: "textArray",
    label: "Дополнительные адреса для счетов",
    helperText: "Письмо со счётом уходит на них и на email контакта выше",
    xs: 12,
    fullWidth: true,
  },
  contractNumber: {
    type: "text",
    label: "Номер договора",
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
  contractDate: {
    type: "date",
    label: "Дата договора",
    xs: 12,
    sm: 6,
    fullWidth: true,
  },
};

function mapRequisitesToFormValues(
  requisites?: OrganizationBillingRequisitesData | null
) {
  if (!requisites) {
    return {
      companyName: "",
      registrationNumber: "",
      vatNumber: "",
      address: "",
      contactName: "",
      contactEmail: "",
      invoiceEmails: [],
      contractNumber: "",
      contractDate: null,
    };
  }

  return {
    companyName: requisites.companyName,
    registrationNumber: requisites.registrationNumber,
    vatNumber: requisites.vatNumber,
    address: requisites.address,
    contactName: requisites.contactName,
    contactEmail: requisites.contactEmail ?? "",
    invoiceEmails: requisites.invoiceEmails,
    contractNumber: requisites.contractNumber,
    contractDate: requisites.contractDate ?? null,
  };
}

/**
 * Вкладка «Реквизиты» организации (спека §4): реквизиты плательщика для
 * счетов за подписку платформы. Отдельная сущность от BillingSeller, там
 * реквизиты продавца, то есть ottkit, здесь покупателя.
 */
export const OrganizationBillingRequisites: FC<
  OrganizationBillingRequisitesProps
> = ({ id }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading, error, refetch } =
    useOrganizationBillingRequisitesQuery(id);

  const [updateRequisites, { loading: updating }] =
    useUpdateOrganizationBillingRequisitesMutation({
      onCompleted: () => {
        enqueueSnackbar("Реквизиты организации сохранены", {
          variant: "success",
        });
        refetch();
      },
      onError: (mutationError) => {
        enqueueSnackbar(
          getErrorMessage(
            getGraphQLErrorCode(mutationError),
            mutationError.message
          ),
          { variant: "error" }
        );
      },
    });

  const requisites = data?.organization?.billingRequisites;
  const initialValues = useMemo(
    () => mapRequisitesToFormValues(requisites),
    [requisites]
  );

  // Формат email проверяется на клиенте тем же кодом, каким его теперь
  // отвергает бэкенд (ORGANIZATION_BILLING_EMAIL_INVALID): ошибка на
  // конкретном поле до отправки, а не общий снекбар после отказа.
  const validate = useMemo(
    () => validateWithZod(makeOrganizationBillingRequisitesValidationSchema()),
    []
  );

  const handleSubmit = async (values: Record<string, any>) => {
    await updateRequisites({ variables: { id, input: values } });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <Card>
      <CardHeader title="Реквизиты плательщика" sx={{ px: 2, pt: 2, pb: 0 }} />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        {!requisites?.filled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Без юрлица и адреса счёт этой организации выставить нельзя.
          </Alert>
        )}
        <FormBuilder
          formId="organization-billing-requisites-form"
          schema={schema}
          initialValues={initialValues}
          editData={requisites}
          onSubmit={handleSubmit}
          validate={validate}
          submitButton
          resetButton={false}
          submitButtonText="Сохранить"
          disabled={updating}
          spacing={2}
          actionsAlign="end"
        />
      </CardContent>
    </Card>
  );
};
